import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Users, Wallet, Banknote, UserPlus, ArrowLeftRight } from 'lucide-react';
import hasRole from '../../utils/hasRole';
import { IS_LAXMI_HOST } from '../../utils/demo';
import { brandFor } from '../../utils/appBrand';
import { useCompanyIdentity } from '../../hooks/useCompanyIdentity';
import RoleShell from '../common/RoleShell';

const iconBtnStyle = { background: 'none', border: 'none', color: 'var(--text)', cursor: 'pointer', padding: 6, display: 'flex' };

const CUSTOMERS = { to: '/', icon: Users, label: 'Customers', end: true, alsoOn: ['/agent'] }; // `/` is the front door; /agent is the older address of the same page
const COLLECTION = { to: '/agent/collections', icon: Banknote, label: 'Collection' };
const LEAD = { to: '/agent/lead', icon: UserPlus, label: 'Lead' };
const BALANCE = { to: '/agent/ledger', icon: Wallet, label: 'Balance Sheet' };

// The collection-agent app: the same frame as every other role (RoleShell). The pages inside keep
// their own layouts and talk to this shell only through the outlet context's setHeader().
export default function AgentAppLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const brand = brandFor(useCompanyIdentity('catv'));
  const [header, setHeader] = useState({ title: 'Agent', showBack: false, menu: null });

  const isRoot = location.pathname === '/' || location.pathname === '/agent';
  const isImpersonating = !!localStorage.getItem('adminToken');
  const impersonatingName = localStorage.getItem('impersonatingName');

  // While viewing (or acting on) a specific customer, the Balance Sheet tab should jump
  // straight to that customer's own ledger instead of the full customer-picker list.
  const customerMatch = location.pathname.match(/^\/agent\/customer\/([^/]+)/);
  const currentCustomerId = customerMatch ? customerMatch[1] : null;
  const ledgerTo = currentCustomerId ? `/agent/customer/${currentCustomerId}/ledger` : '/agent/ledger';

  const handleLogout = async () => {
    // An admin impersonating an agent must land back on the staff CRM login, not the
    // agent-branded one — otherwise leftover admin* keys also linger in localStorage.
    const wasImpersonating = isImpersonating;
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminRefreshToken');
    localStorage.removeItem('impersonatingName');
    await logout();
    navigate(wasImpersonating ? '/login' : '/agent/login');
  };

  const handleSwitchBack = () => {
    const adminToken = localStorage.getItem('adminToken');
    const adminRefresh = localStorage.getItem('adminRefreshToken');
    localStorage.setItem('token', adminToken);
    if (adminRefresh) localStorage.setItem('refreshToken', adminRefresh);
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminRefreshToken');
    localStorage.removeItem('impersonatingName');
    window.location.href = '/agent-collection';
  };

  // Phone bottom bar keeps today's rule: Balance Sheet is hidden on the first page and appears inside a customer.
  const tabs = isRoot ? [CUSTOMERS, COLLECTION, LEAD] : [CUSTOMERS, { ...BALANCE, to: ledgerTo }];

  // Laxmi keeps its own agent logo; the demo and every operator show their own company logo.
  const logoSrc = IS_LAXMI_HOST ? '/agent-logo.png?v=2' : brand.logo;

  return (
    <RoleShell
      items={[CUSTOMERS, COLLECTION, LEAD, BALANCE]}
      tabs={tabs}
      title={header.title}
      onBack={header.showBack ? () => navigate(-1) : null}
      logo={<img src={logoSrc} alt={brand.name} style={{ height: 56, maxWidth: '100%', objectFit: 'contain' }} />}
      topLogo={<img src={logoSrc} alt="" />}
      contentMaxWidth={480}
      flush
      onLogout={handleLogout}
      topExtras={(
        <>
          {header.menu && <span className="shell-menu-slot">{header.menu}</span>}
          {isRoot && hasRole(user, 'technical') && (
            <button onClick={() => navigate('/technical')} style={iconBtnStyle} title="Switch to Technical"><ArrowLeftRight size={18} /></button>
          )}
        </>
      )}
      banner={isImpersonating && (
        <div style={{
          background: '#f59e0b', color: '#1c1917', fontSize: '.78rem', fontWeight: 700,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap',
          padding: '8px 14px',
        }}>
          <span>👤 Viewing as agent: {impersonatingName}</span>
          <button onClick={handleSwitchBack} style={{ background: '#1c1917', color: '#fff', border: 'none', borderRadius: 6, padding: '4px 12px', cursor: 'pointer', fontSize: '.75rem', fontWeight: 700 }}>
            Switch Back
          </button>
        </div>
      )}
    >
      <Outlet context={{ setHeader }} />
    </RoleShell>
  );
}
